/**
 * This module manages a user's reporting preferences
 */

import Marionette from "backbone.marionette";
import Radio from "backbone.radio";

import Collection from "../collections/reporting-preferences";
import Model from "../models/reporting-preferences";

import { generalizeRouteName } from "../utils";

const globalChannel = Radio.channel("global");
const reportChannel = Radio.channel("report");

const EVENT = {
  COLUMNS_ORDERED: "report-columns-reordered",
  PREFERENCES_ADDED: "reporting-preferences-updated",
  PREFERENCES_REMOVED: "reporting-preferences-updated",
};

class ReportingPreferences extends Marionette.Object {
  initialize() {
    // set request handlers
    reportChannel.reply("preferences:find", this.find, this);
    reportChannel.reply("report-columns-moved", this.onColumnsMoved, this);
    reportChannel.reply("reporting-preferences-selected", this.add, this);
    reportChannel.reply("reporting-preferences-removed", this.remove, this);
    // reportChannel.reply("report-filters-reset", this._onFiltersReset, this);

    // load data
    this.collection = new Collection();
    this.collection.promise.done(this.onLoad.bind(this));
    this.collection.promise.fail(this.onLoadFailure.bind(this));
  }

  onLoad() {
    // listen for further changes to model
    this.listenTo(this.collection, "update", this._onCollectionUpdate);

    // notify interested parties
    reportChannel.trigger("reporting-preferences-loaded", this.collection);
  }

  onLoadFailure(xhr) {
    console.warn("[%s] Unable to load report preferences.", xhr.status);

    // show error
    globalChannel.trigger("status:show", {
      type: "warning",
      message:
        "We were unable to load your report preferences. View customizations may not appear as expected.",
    });
  }

  find(route) {
    const _route = generalizeRouteName(route);
    const found = this.collection.findWhere({ name: _route });
    return found ? found : null;
  }

  onColumnsMoved(report) {
    // remove report preference when it exists
    this.remove(report, true);

    const _route = generalizeRouteName(report.route);

    // create new reporting preference
    const preference = new Model({
      name: _route,
      columns: report.columns,
    });

    // event to fire on success
    const eventName = `${report.route}.${EVENT.COLUMNS_ORDERED}`;

    // add it to the collection - this will trigger the DT to reload
    this.collection.add(preference, { eventName });
  }

  add(report) {
    this._updateHistory(report);

    // remove report preference when it exists
    this.remove(report, true);

    const _route = generalizeRouteName(report.route);

    // create new reporting preference
    const preference = new Model({
      name: _route,
      columns: report.columns,
      params: report.params,
    });

    // event to fire on success
    const eventName = `${report.route}.${EVENT.PREFERENCES_ADDED}`;

    // add it to the collection
    this.collection.add(preference, { eventName });
  }

  remove(report, supressEvents = false) {
    this._updateHistory(report);

    // event to fire on success
    const eventName = `${report.route}.${EVENT.PREFERENCES_REMOVED}`;

    // search collection for reporting preference
    const found = this.find(report.route);
    if (found) {
      // remove it from the collection when found
      this.collection.remove(found, {
        silent: supressEvents ? true : false,
        eventName,
      });
    }
  }

  _onCollectionUpdate(collection, options) {
    // get added model
    let model = null;
    const eventName = options.eventName ? options.eventName : null;

    // handle coll additions and removals (we don't use merges)
    if (options.add) {
      // grab model that was added
      model = options.changes.added[0];
    }

    collection.save({
      success() {
        // show success message
        globalChannel.trigger("status:show", {
          type: "success",
          message: "Your customized view preferences have been saved.",
          autoDismissTimeout: 2000,
        });

        // notify interested parties
        if (eventName) {
          reportChannel.trigger(eventName, model);
        }
      },
      error() {
        // show error alert
        globalChannel.trigger("status:show", {
          autoDismiss: false,
          type: "warning",
          message:
            "Your customized view preferences could not be saved at this time. Please try again later.",
        });
      },
    });
  }

  _updateHistory(report) {
    const historyModel = reportChannel.request("history:find", report.route);
    if (historyModel) {
      historyModel.set({
        customColumns: report.columns || null,
        customColumnsCreated: report.columns ? Date.now() : null,
      });
      reportChannel.trigger("history:add", historyModel);
    }
  }

  // _onFiltersReset(route) {
  // 	this.remove(route);
  // }
}

export { ReportingPreferences as default };
